import React, { useState } from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import '../common.css';

// Transportation type options
const transportationOptions = [
  { id: "air", label: "Air travel" },
  { id: "bus", label: "Bus/coach" },
  { id: "train", label: "Train" },
  { id: "personal", label: "Personal vehicles" },
  { id: "rental", label: "Rental vehicles" },
  { id: "public", label: "Public transportation" },
  { id: "rideshare", label: "Rideshare services" },
  { id: "specialty", label: "Specialty transport" },
  { id: "cargo", label: "Cargo/freight shipping" },
  { id: "other", label: "Other" }
];

// Equipment volume options
const equipmentVolumeOptions = [
  { id: "minimal", label: "Minimal (few suitcases/bags)" },
  { id: "moderate", label: "Moderate (several cases/equipment)" },
  { id: "substantial", label: "Substantial (requires dedicated transport)" },
  { id: "extensive", label: "Extensive (requires specialized logistics)" }
];

// Accommodation levels
const accommodationLevels = [
  { value: 0, label: "Economy" },
  { value: 33, label: "Mid-range" },
  { value: 66, label: "Premium" },
  { value: 100, label: "Luxury" }
];

// Accommodation distance units
const distanceUnits = [
  { value: "minutes", label: "minutes" },
  { value: "miles", label: "miles" },
  { value: "km", label: "km" }
];

const LogisticsTransportation = ({ formData, onInputChange, onNext, onPrev, error }) => {
  const [validationError, setValidationError] = useState('');
  
  // Handle checkbox changes for multi-select options
  const handleMultiSelectChange = (field, value, isChecked) => {
    const currentValues = formData[field] || [];
    let newValues;
    
    if (isChecked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(item => item !== value);
    }
    
    onInputChange({ [field]: newValues });
    
    // Clear validation error if the required field is now filled
    if (field === 'transportationTypes' && newValues.length > 0) {
      setValidationError('');
    }
  };

  // Handle input changes for text fields
  const handleInputChange = (field, value) => {
    onInputChange({ [field]: value });
  };

  // Check if a value is selected in a multi-select list
  const isSelected = (field, value) => {
    return (formData[field] || []).includes(value);
  };
  
  // Validate form before proceeding to next step
  const validateForm = () => {
    // Check if transportation types are selected
    if (!formData.transportationTypes || formData.transportationTypes.length === 0) {
      setValidationError('Please select at least one transportation type');
      return false;
    }
    
    return true;
  };
  
  // Handle next button click
  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <Card className="dark-main-card text-white mb-4">
      <Card.Header className="dark-card-header border-bottom">
        <h4 className="mb-0 text-white">Plan Your Movement Strategy</h4>
      </Card.Header>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="dark-alert mb-4">{error}</Alert>}
        {validationError && (
          <Alert variant="warning" className="dark-alert mb-4">
            <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
            {validationError}
          </Alert>
        )}
        
        <Form>
          {/* Transportation Type Section */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Transportation Type <span className="text-danger">*</span></h5>
            
            <Row>
              {transportationOptions.slice(0, transportationOptions.length - 1).map((option) => (
                <Col md={6} key={option.id} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id={`transport-${option.id}`}
                    className="dark-checkbox"
                    label={option.label}
                    checked={isSelected('transportationTypes', option.id)}
                    onChange={(e) => handleMultiSelectChange('transportationTypes', option.id, e.target.checked)}
                  />
                </Col>
              ))}
            </Row>
            
            <Row className="mt-2">
              <Col md={6} className="mb-2">
                <Form.Check
                  type="checkbox"
                  id="transport-other"
                  className="dark-checkbox"
                  label="Other"
                  checked={isSelected('transportationTypes', 'other')}
                  onChange={(e) => handleMultiSelectChange('transportationTypes', 'other', e.target.checked)}
                />
              </Col>
              <Col md={6}>
                {isSelected('transportationTypes', 'other') && (
                  <Form.Control
                    type="text"
                    className="dark-input"
                    placeholder="Specify other transportation"
                    value={formData.transportationOther || ''}
                    onChange={(e) => handleInputChange('transportationOther', e.target.value)}
                  />
                )}
              </Col>
            </Row>
          </div>
          
          {/* Group Size Details */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Group Size Details</h5>
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Core team size</Form.Label>
                  <Form.Control
                    type="number"
                    className="dark-input"
                    min="1"
                    placeholder="Number of core team members"
                    value={formData.coreTeamSize || ''}
                    onChange={(e) => handleInputChange('coreTeamSize', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Additional personnel</Form.Label>
                  <Form.Control
                    type="number"
                    className="dark-input"
                    min="0"
                    placeholder="Number of additional personnel"
                    value={formData.additionalPersonnel || ''}
                    onChange={(e) => handleInputChange('additionalPersonnel', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Equipment volume</Form.Label>
              {equipmentVolumeOptions.map(option => (
                <Form.Check
                  key={option.id}
                  type="radio"
                  id={`equipment-${option.id}`}
                  className="dark-checkbox"
                  label={option.label}
                  name="equipmentVolume"
                  value={option.id}
                  checked={formData.equipmentVolume === option.id}
                  onChange={(e) => handleInputChange('equipmentVolume', e.target.value)}
                />
              ))}
            </Form.Group>
          </div>
          
          {/* Special Transportation Needs */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Special Transportation Needs</h5>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Equipment requiring special handling</Form.Label>
              <Form.Control
                type="text"
                className="dark-input"
                placeholder="List any equipment needing special handling"
                value={formData.specialHandlingEquipment || ''}
                onChange={(e) => handleInputChange('specialHandlingEquipment', e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Oversized items</Form.Label>
              <Form.Control
                type="text"
                className="dark-input"
                placeholder="List any oversized items"
                value={formData.oversizedItems || ''}
                onChange={(e) => handleInputChange('oversizedItems', e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Fragile/sensitive items</Form.Label>
              <Form.Control
                type="text"
                className="dark-input"
                placeholder="List any fragile or sensitive items"
                value={formData.fragileItems || ''}
                onChange={(e) => handleInputChange('fragileItems', e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Special transportation permits needed</Form.Label>
              <Form.Control
                type="text"
                className="dark-input"
                placeholder="List any required permits"
                value={formData.transportationPermits || ''}
                onChange={(e) => handleInputChange('transportationPermits', e.target.value)}
              />
            </Form.Group>
          </div>
          
          {/* Accommodation Requirements */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Accommodation Requirements</h5>
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Standard rooms needed</Form.Label>
                  <Form.Control
                    type="number"
                    className="dark-input"
                    min="0"
                    placeholder="Number of standard rooms"
                    value={formData.standardRooms || ''}
                    onChange={(e) => handleInputChange('standardRooms', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Suite/upgraded rooms needed</Form.Label>
                  <Form.Control
                    type="number"
                    className="dark-input"
                    min="0"
                    placeholder="Number of suites or upgraded rooms"
                    value={formData.upgradeRooms || ''}
                    onChange={(e) => handleInputChange('upgradeRooms', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Special room requirements</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                className="dark-input"
                placeholder="Any special room requirements (e.g., accessibility needs, connecting rooms)"
                value={formData.specialRoomRequirements || ''}
                onChange={(e) => handleInputChange('specialRoomRequirements', e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Preferred accommodation level</Form.Label>
              <Form.Range
                min={0}
                max={100}
                step={1}
                value={formData.accommodationLevel || 33}
                onChange={(e) => handleInputChange('accommodationLevel', e.target.value)}
              />
              <div className="d-flex justify-content-between">
                {accommodationLevels.map(level => (
                  <div 
                    key={level.value} 
                    className={`text-light ${Math.abs(formData.accommodationLevel - level.value) < 17 ? 'fw-bold' : ''}`}
                  >
                    {level.label}
                  </div>
                ))}
              </div>
            </Form.Group>
            
            <Form.Group>
              <Form.Label className="text-white">Distance from venue</Form.Label>
              <div className="d-flex">
                <span className="me-2 text-light align-self-center">Must be within</span>
                <Form.Control
                  type="number"
                  className="dark-input me-2"
                  min="0"
                  style={{ width: '80px' }}
                  value={formData.venueDistance || ''}
                  onChange={(e) => handleInputChange('venueDistance', e.target.value)}
                />
                <Form.Select
                  className="dark-input"
                  style={{ width: '120px' }}
                  value={formData.venueDistanceUnit || 'minutes'}
                  onChange={(e) => handleInputChange('venueDistanceUnit', e.target.value)}
                >
                  {distanceUnits.map(unit => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </Form.Select>
                <span className="ms-2 text-light align-self-center">of venue</span>
              </div>
            </Form.Group>
          </div>
          
          {/* Special Logistical Considerations */}
          <Form.Group className="mb-4">
            <Form.Label className="text-white">Special Logistical Considerations</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="dark-input"
              placeholder="Any other transportation, accommodation, or logistics considerations"
              value={formData.specialLogistics || ''}
              onChange={(e) => handleInputChange('specialLogistics', e.target.value)}
            />
          </Form.Group>
          
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-light"
              className="dark-outline-button"
              onClick={onPrev}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
              Back
            </Button>
            <Button
              className="dark-primary-button"
              onClick={handleNext}
            >
              Continue
              <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default LogisticsTransportation;